import React, { useState, useEffect, useMemo } from "react";
import { Namespace, useApiErrorMessage, useT } from "@racoon/i18n";
import { skipToken } from "@reduxjs/toolkit/query";
import {
  Button,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  useToast,
  ModalFooter,
  SearchableSelect,
  Separator,
} from "@racoon/ui";
import {
  useCreateCustomGroupWithGrantsMutation,
  useListGroupGrantsQuery,
  useUpdateCustomGroupMutation,
  useGrantRoleAccessToScopeMutation,
  useUpdateResourceAccessGrantMutation,
  useRevokeGrantMutation,
  useBulkUpdateGrantsOrganizationMutation,
} from "../../../../shared/api/custom-groups/custom-groups.api";
import { useListOrganizationSubtreeQuery } from "../../../../shared/api/organization/organization.grpc";

import { Resource } from "@racoon/lib";
import { ConnectProto, type ApiError } from "@racoon/api";
import { useAuthSession } from "~/shared/hooks/auth.session.admin";
import { organizationTreeOptions } from "~/shared/lib/org-tree";

interface CreateAccessGroupFormProps {
  initialData?: ConnectProto.CustomGroup;
  onClose: () => void;
  onSuccess?: () => void;
  onArchive?: (group: ConnectProto.CustomGroup) => void;
}

const generateModules = () => {
  const resourceEntries = Object.entries(Resource).filter(
    ([key]) => key.startsWith("Res") && key !== "Res",
  );

  return resourceEntries.map(([key, value]) => {
    const translationKeyName = key.charAt(0).toLowerCase() + key.slice(1);
    return {
      id: value,
      translationKey: `access_groups.module.${translationKeyName}`,
    };
  });
};

const MODULES = generateModules();

const ACCESS_LEVELS = [
  { id: "none", translationKey: "access_groups.level.none" },
  { id: "viewer", translationKey: "access_groups.level.viewer" },
  { id: "editor", translationKey: "access_groups.level.editor" },
  { id: "manager", translationKey: "access_groups.level.manager" },
];

export const CreateAccessGroupForm: React.FC<CreateAccessGroupFormProps> = ({
  initialData,
  onClose,
  onSuccess,
  onArchive,
}) => {
  const t = useT();
  const { toast } = useToast();
  const describeError = useApiErrorMessage();
  const [createGroup, { isLoading: isCreating }] = useCreateCustomGroupWithGrantsMutation();
  const [updateGroup, { isLoading: isUpdating }] = useUpdateCustomGroupMutation();
  const [grantAccess] = useGrantRoleAccessToScopeMutation();
  const [updateGrant] = useUpdateResourceAccessGrantMutation();
  const [revokeGrant] = useRevokeGrantMutation();
  const [bulkUpdateGrantsOrganization, { isLoading: isBulkUpdatingOrganization }] =
    useBulkUpdateGrantsOrganizationMutation();

  const isEdit = !!initialData;
  const initialGroupId = initialData ? BigInt(initialData.id) : undefined;

  const { activeCompanyId: currentOrgId } = useAuthSession();
  // The group's own organization: a holding edits its companies' groups, so an existing
  // group keeps its owner and a new one belongs to the active company.
  const ownerOrgId = isEdit
    ? BigInt(initialData!.ownerOrganizationId)
    : currentOrgId
      ? BigInt(currentOrgId)
      : undefined;

  const {
    data: grantsData,
    isLoading: isGrantsLoading,
    refetch: refetchGrants,
  } = useListGroupGrantsQuery(
    { customGroupId: initialGroupId!, organizationId: ownerOrgId! },
    { skip: !isEdit || !ownerOrgId, refetchOnMountOrArgChange: true },
  );

  const [name, setName] = useState("");

  // int64 ids stay strings until the request: Number would round past 2^53.
  const [selectedOrgId, setSelectedOrgId] = useState<string | null>(
    ownerOrgId !== undefined ? String(ownerOrgId) : null,
  );

  // A group reaches its node and every node below it, so any node of the owner's
  // subtree can be picked, at any depth.
  const { data: subtreeData, isLoading: isSubtreeLoading } = useListOrganizationSubtreeQuery(
    ownerOrgId !== undefined ? { organizationId: ownerOrgId } : skipToken,
  );
  const orgOptions = useMemo(
    () => organizationTreeOptions(subtreeData?.organizations ?? []),
    [subtreeData],
  );

  const [grants, setGrants] = useState<Record<string, string>>(
    MODULES.reduce((acc, module) => ({ ...acc, [module.id]: "none" }), {}),
  );

  useEffect(() => {
    if (isEdit && initialData) {
      setName(initialData.name);
    }
  }, [isEdit, initialData]);

  useEffect(() => {
    if (!isEdit || !initialData) return;

    const firstGrantOrgId = grantsData?.grants?.[0]?.organizationId;
    const fallbackOwnerOrgId = initialData.ownerOrganizationId;
    const orgIdToDisplay = firstGrantOrgId ?? fallbackOwnerOrgId;

    if (orgIdToDisplay !== undefined && orgIdToDisplay !== null) {
      setSelectedOrgId(String(orgIdToDisplay));
    }
  }, [isEdit, initialData, grantsData]);

  useEffect(() => {
    if (isEdit && grantsData) {
      const newGrants = MODULES.reduce(
        (acc, module) => ({ ...acc, [module.id]: "none" }),
        {} as Record<string, string>,
      );
      grantsData.grants.forEach((g) => {
        newGrants[g.resourceType] = g.accessLevel;
      });
      setGrants(newGrants);
    }
  }, [isEdit, grantsData]);

  const handleGrantChange = (moduleId: string, levelId: string) => {
    setGrants((prev) => ({ ...prev, [moduleId]: levelId }));
  };

  const handleSave = async () => {
    if (!name.trim()) return;
    const finalOwnerOrgId = ownerOrgId;
    if (!finalOwnerOrgId) return;

    const targetOrgId = selectedOrgId ? BigInt(selectedOrgId) : finalOwnerOrgId;

    if (isEdit) {
      try {
        if (name.trim() !== initialData?.name) {
          await updateGroup({ id: initialGroupId!, name: name.trim() }).unwrap();
        }

        const previousOrgIdRaw =
          grantsData?.grants?.[0]?.organizationId ?? initialData?.ownerOrganizationId;
        const previousOrgId =
          previousOrgIdRaw !== undefined && previousOrgIdRaw !== null
            ? BigInt(previousOrgIdRaw)
            : undefined;
        if (previousOrgId && previousOrgId !== targetOrgId) {
          await bulkUpdateGrantsOrganization({
            customGroupId: initialGroupId!,
            organizationId: targetOrgId,
          }).unwrap();
        }

        // Sync grants
        const currentGrants = grantsData?.grants || [];
        const currentGrantsMap = new Map(currentGrants.map((g) => [g.resourceType, g]));

        for (const module of MODULES) {
          const newLevel = grants[module.id];
          const existingGrant = currentGrantsMap.get(module.id);

          if (newLevel === "none") {
            if (existingGrant) {
              await revokeGrant({ id: existingGrant.id }).unwrap();
            }
          } else {
            if (existingGrant) {
              if (existingGrant.accessLevel !== newLevel) {
                await updateGrant({ id: existingGrant.id, accessLevel: newLevel }).unwrap();
              }
            } else {
              await grantAccess({
                customGroupId: initialGroupId!,
                organizationId: targetOrgId,
                resourceType: module.id,
                accessLevel: newLevel,
              }).unwrap();
            }
          }
        }

        toast({ title: t(Namespace.Settings, "access_groups.dialog.updateSuccess") });
        onSuccess?.();
        refetchGrants();
        onClose();
      } catch (error) {
        toast({
          title: t(Namespace.Settings, "access_groups.dialog.updateError"),
          description: describeError(error as Partial<ApiError>),
          variant: "destructive",
        });
      }
    } else {
      const formattedGrants = Object.entries(grants)
        .filter(([, level]) => level !== "none")
        .map(
          ([resourceType, accessLevel]) =>
            ({
              organizationId: targetOrgId,
              resourceType: resourceType,
              accessLevel: accessLevel,
            }) as any,
        );

      try {
        await createGroup({
          name: name.trim(),
          ownerOrganizationId: finalOwnerOrgId,
          grants: formattedGrants,
        }).unwrap();

        toast({
          title: t(Namespace.Settings, "access_groups.dialog.createSuccess" as any),
        });
        onSuccess?.();
        onClose();
      } catch (error) {
        toast({
          title: t(Namespace.Settings, "access_groups.dialog.error" as any),
          description: describeError(error as Partial<ApiError>),
          variant: "destructive",
        });
      }
    }
  };

  const isLoading = isCreating || isUpdating || isGrantsLoading || isBulkUpdatingOrganization;

  return (
    <div className="flex flex-col h-full">
      <div className="space-y-4 py-4 flex-1 overflow-y-auto">
        <div className="flex px-4 justify-between gap-32">
          <div className="flex flex-1 gap-2 items-center">
            <label className="text-sm flex-[0.3] font-medium flex">
              {t(Namespace.Settings, "access_groups.dialog.name" as any)}
            </label>
            <Input
              value={name}
              className="flex flex-1 max-h-8"
              onChange={(e) => setName(e.target.value)}
              placeholder={t(Namespace.Settings, "access_groups.dialog.namePlaceholder" as any)}
            />
          </div>
          <div className="flex flex-[0.5] gap-2 items-center">
            <label className="text-sm flex text-center align-center items-center font-medium whitespace-nowrap">
              {t(Namespace.Settings, "access_groups.dialog.suborganization")}
            </label>
            <SearchableSelect
              id="access-group-org"
              value={selectedOrgId ?? ""}
              options={orgOptions}
              loading={isSubtreeLoading}
              onChange={setSelectedOrgId}
              placeholder={t(Namespace.Settings, "access_groups.dialog.selectOrganization")}
              searchPlaceholder={t(Namespace.Settings, "access_groups.dialog.searchOrganization")}
              size="sm"
            />
          </div>
        </div>
        <Separator />
        <div className="flex px-4 flex-col gap-3 mt-2">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-x-16 gap-y-4">
            {MODULES.map((module) => (
              <div key={module.id} className="flex items-center justify-between">
                <span className="text-sm font-medium mr-2">
                  {t(Namespace.Settings, module.translationKey as any)}
                </span>
                <div className="w-[140px] shrink-0">
                  <Select
                    value={grants[module.id]}
                    onValueChange={(val) => handleGrantChange(module.id, val)}
                  >
                    <SelectTrigger className="bg-white h-8">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {ACCESS_LEVELS.map((level) => (
                        <SelectItem key={level.id} value={level.id}>
                          {t(Namespace.Settings, level.translationKey as any)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <ModalFooter className="flex-col-reverse sm:flex-row sm:justify-end gap-2 border-t mt-auto">
        {initialData && onArchive && (
          <Button
            disabled={isLoading}
            variant="opacityRedWithOutline"
            onClick={() => onArchive(initialData)}
            className="sm:mr-auto"
          >
            {t(Namespace.Buttons, "archive")}
          </Button>
        )}
        <Button
          disabled={!name.trim() || isLoading}
          variant="opacityBlueWithOutline"
          onClick={handleSave}
          className=""
        >
          {isEdit
            ? t(Namespace.Settings, "access_groups.dialog.saveButton" as any)
            : t(Namespace.Settings, "access_groups.dialog.createButton" as any)}
        </Button>
      </ModalFooter>
    </div>
  );
};
